import { Button } from '@mantine/core';
import { Globe, MapPin, Phone, Star } from 'lucide-react';
import type { FC } from 'react';
import { useHostContext } from '@/context/HostContext.tsx';
import type { BusinessProfile } from '@/pages/BusinessConfirmation/BusinessConfirmation.types.ts';
import ConfirmationHeader from '@/pages/BusinessConfirmation/components/ConfirmationHeader';

type MapsCardProps = {
	profile: BusinessProfile;
	onConfirm: () => void;
	onNo: () => void;
};

const MapsCard: FC<MapsCardProps> = ({ profile: p, onConfirm, onNo }) => {
	const { localization } = useHostContext();

	return (
		<div
			className="flex min-h-[360px] flex-1 flex-col items-center justify-center py-4 sm:py-6"
			data-testid="business-confirmation-maps"
		>
			<div className="w-full max-w-xl">
				<ConfirmationHeader
					subtitle={localization.translate(
						'creator',
						'businessConfirmation.mapsCard.subtitle'
					)}
					title={localization.translate(
						'creator',
						'businessConfirmation.mapsCard.title'
					)}
				/>

				<div className="overflow-hidden rounded-xl border border-slate-200">
					{p.photoUrl && (
						<div className="h-44 w-full overflow-hidden bg-slate-100">
							<img
								alt={p.name}
								className="h-full w-full object-cover"
								src={p.photoUrl}
							/>
						</div>
					)}
					<div className="px-4 py-3">
						<p
							className="text-base font-bold text-black"
							data-testid="business-confirmation-business-name"
						>
							{p.name}
						</p>
						{p.rating ? (
							<div className="mt-1 flex items-center gap-1 text-sm text-slate-500">
								<Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
								<span>{p.rating}</span>
								{p.totalRatings ? (
									<span>
										(
										{localization.translate(
											'creator',
											'businessConfirmation.mapsCard.reviews',
											{ count: String(p.totalRatings) }
										)}
										)
									</span>
								) : null}
							</div>
						) : null}
					</div>
					<div className="space-y-2 border-t border-slate-100 px-4 py-3">
						{p.address ? (
							<div className="flex items-center gap-2 text-sm text-slate-600">
								<MapPin className="h-4 w-4 flex-shrink-0 text-slate-400" />
								<span>{p.address}</span>
							</div>
						) : null}
						{p.phone ? (
							<div className="flex items-center gap-2 text-sm text-slate-600">
								<Phone className="h-4 w-4 flex-shrink-0 text-slate-400" />
								<span>{p.phone}</span>
							</div>
						) : null}
						{p.website ? (
							<div className="flex items-center gap-2 text-sm text-slate-600">
								<Globe className="h-4 w-4 flex-shrink-0 text-slate-400" />
								<a
									className="text-black underline"
									href={p.website}
									rel="noopener noreferrer"
									target="_blank"
								>
									{p.website}
								</a>
							</div>
						) : null}
					</div>
				</div>

				<div className="mt-4 flex flex-wrap items-center justify-end gap-3">
					<Button
						data-testid="business-confirmation-confirm"
						onClick={onConfirm}
						radius="xl"
						size="md"
					>
						{localization.translate(
							'creator',
							'businessConfirmation.mapsCard.confirmButton'
						)}
					</Button>
					<Button
						className="hover:!bg-transparent"
						data-testid="business-confirmation-no"
						onClick={onNo}
						radius="xl"
						size="md"
						variant="outline"
					>
						{localization.translate(
							'creator',
							'businessConfirmation.mapsCard.denyButton'
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default MapsCard;
